/**
 * Agency deal — post-acceptance pipeline ("dealflow").
 *
 * Once a prospect commits, the agency tracks the transaction to close through a
 * tailored milestone checklist (rental vs sale) plus the agency's own money
 * (fee invoiced / commission paid). Stored at
 * `workspaces/{agencyWs}/agency_deals/{dealId}`, client-writable like leads.
 *
 * `mandateId` is the optional seam back to an owner mandate (Phase D — propose
 * changes to the owner — builds on it; not used yet).
 */

import { Timestamp } from '@angular/fire/firestore';
import { LeadContact } from './agency-lead';

export type DealType = 'sale' | 'rental';
export type DealStatus = 'active' | 'completed' | 'fell_through' | 'cancelled';

export interface DealMilestone {
  key: string;
  order: number;
  done: boolean;
  dueAt?: Timestamp | Date;
  doneAt?: Timestamp | Date;
}

export interface DealFinancials {
  currency: 'EUR';
  transactionPrice?: number;
  agencyFeeAmount?: number;
  commissionPct?: number;
  invoiced?: boolean;
  invoicedAt?: Timestamp | Date;
  paid?: boolean;
  paidAt?: Timestamp | Date;
}

export interface AgencyDeal {
  dealId: string;
  workspaceId: string;
  type: DealType;
  status: DealStatus;
  /** Denormalised label (prospect / property name) for list rows. */
  title?: string;
  leadId?: string;
  propertyId?: string;
  mandateId?: string;
  contacts: LeadContact[];
  milestones: DealMilestone[];
  financials: DealFinancials;
  assignedAgentId?: string;
  closedAt?: Timestamp | Date;

  // Soft-delete (archive) — hidden from default views, restorable for 30 days.
  isDeleted?: boolean;
  deletedAt?: Timestamp | Date;
  permanentDeletionDate?: Timestamp | Date;
  deletedBy?: string;

  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

export const DEAL_TYPES: readonly DealType[] = ['sale', 'rental'];
export const DEAL_STATUSES: readonly DealStatus[] = ['active', 'completed', 'fell_through', 'cancelled'];

/** Ordered milestone templates. `fee_invoiced` / `commission_paid` close both flows. */
export const RENTAL_MILESTONES: readonly { key: string; order: number }[] = [
  { key: 'agreement', order: 0 },
  { key: 'lease_drafted', order: 1 },
  { key: 'lease_signed', order: 2 },
  { key: 'deposit_received', order: 3 },
  { key: 'etat_des_lieux', order: 4 },
  { key: 'keys_handed', order: 5 },
  { key: 'fee_invoiced', order: 6 },
  { key: 'commission_paid', order: 7 },
];

export const SALE_MILESTONES: readonly { key: string; order: number }[] = [
  { key: 'offer_accepted', order: 0 },
  { key: 'compromis', order: 1 },
  { key: 'notary', order: 2 },
  { key: 'acte', order: 3 },
  { key: 'fee_invoiced', order: 4 },
  { key: 'commission_paid', order: 5 },
];

export function defaultDealMilestones(type: DealType): DealMilestone[] {
  return (type === 'rental' ? RENTAL_MILESTONES : SALE_MILESTONES).map((m) => ({
    key: m.key,
    order: m.order,
    done: false,
  }));
}

/** Completion percentage (0–100) from the milestone checklist. */
export function dealProgress(deal: Pick<AgencyDeal, 'milestones'>): number {
  const ms = deal.milestones ?? [];
  if (!ms.length) return 0;
  return Math.round((ms.filter((m) => m.done).length / ms.length) * 100);
}

/** The lowest-order milestone still open — the deal's "next step". */
export function nextDealMilestone(deal: Pick<AgencyDeal, 'milestones'>): DealMilestone | undefined {
  return [...(deal.milestones ?? [])].filter((m) => !m.done).sort((a, b) => a.order - b.order)[0];
}

/** Derive the deal type from a lead's intent (buy → sale, rent → rental). */
export function dealTypeFromIntent(intent: 'buy' | 'rent'): DealType {
  return intent === 'buy' ? 'sale' : 'rental';
}
